import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    Typography,
    Radio,
    Checkbox,
    FormControlLabel,
    Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { XClientDataGrid, Loadable } from "../../common";
import { useStatePtr } from "@pure-ptr/react";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { createStringDataField, createPlainDateField } from "../../util/format";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Field } from "../../common/dataTableHeader/DataTableHeader";

const DEFAULT_SORT = [{ id: "id", direction: "asc" }];
const HARDCODED_PDF_BASE64 = "JVBERi0xLjQKJcfsj6IKMSAwIG9iago8PAovVHlwZSAvQ2F0YWxvZwovUGFnZXMgMiAwIFIKPj4KZW5kb2JqCjIgMCBvYmoKPDwKL1R5cGUgL1BhZ2VzCi9LaWRzIFszIDAgUl0KL0NvdW50IDEKPj4KZW5kb2JqCjMgMCBvYmoKPDwKL1R5cGUgL1BhZ2UKL1BhcmVudCAyIDAgUgovTWVkaWFCb3ggWzAgMCAzMDAgMTQ0XQovQ29udGVudHMgNCAwIFIKL1Jlc291cmNlcyA8PAovRm9udCA8PAovRjEgNSAwIFIKPj4KPj4KPj4KZW5kb2JqCjQgMCBvYmoKPDwKL0xlbmd0aCA0NAo+PgpzdHJlYW0KQlQKL0YxIDEyIFRmCjEwMCA3MCBUZAooSGVsbG8gUERGIFRlc3QpIFRqCkVUCmVuZHN0cmVhbQplbmRvYmoKNSAwIG9iago8PAovVHlwZSAvRm9udAovU3VidHlwZSAvVHlwZTEKL0Jhc2VGb250IC9IZWx2ZXRpY2EKPj4KZW5kb2JqCnhyZWYKMCA2CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAwMDAxMCAwMDAwMCBuIAowMDAwMDAwMDYxIDAwMDAwIG4gCjAwMDAwMDAxMTYgMDAwMDAgbiAKMDAwMDAwMDI0MSAwMDAwMCBuIAowMDAwMDAwMzM1IDAwMDAwIG4gCnRyYWlsZXIKPDwKL1NpemUgNgovUm9vdCAxIDAgUgo+PgpzdGFydHhyZWYKMzc4CiUlRU9G"
interface Attachment {
    id: string;
    resourceId: string;
    resourceType: string;
    attachmentName: string;
    attachmentData: string;
}
interface Props {
    open: boolean;
    attachments: Attachment[];
    onClose: () => void;
    onConfirm: (attachments: any) => void;
    isDownloading: boolean;
}

const base64ToBlob = (base64, contentType) => {

        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);

        return new Blob([byteArray], { type: contentType });
    };

   const printPdfBlob = (blob) => {
        const blobUrl = URL.createObjectURL(blob);

        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "none";

        iframe.src = blobUrl;

        document.body.appendChild(iframe);

        iframe.onload = () => {
            const iframeWindow = iframe.contentWindow;

            if (!iframeWindow) return;

            iframeWindow.focus();
            iframeWindow.print();

            iframeWindow.onafterprint = () => {
                document.body.removeChild(iframe);
                URL.revokeObjectURL(blobUrl);
            };
        };
    };

export function AttachmentDialog({
    open,
    attachments,
    onClose,
    onConfirm,
    isDownloading,
}: Props) {
    const hasAttachments = attachments.length > 0;
    const sortPtr = useStatePtr(DEFAULT_SORT);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const handleSelectToggle = (id: string) => {
        setSelectedIds((prev) =>
            prev.includes(id)
                ? prev.filter((selectedId) => selectedId !== id)
                : [...prev, id],
        );
    };

const handlePreview = (params: any) => {
    const base64 = params?.row?.attachMentData;
    const fileType = params?.row?.attachMentType || "application/pdf";
    if (!base64) {
        console.error("No attachment data found");
        return;
    }
    // For demo preview
    if (fileType === "text/html") {
        const blob = base64ToBlob(HARDCODED_PDF_BASE64, "application/pdf");
        printPdfBlob(blob);
        return;
    }
    try {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length)
            .fill(0)
            .map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: fileType });
        const url = URL.createObjectURL(blob);
        window.open(url);
    } catch (error) {
        console.error("Error previewing file:", error);
    }
};
    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedIds(attachments.map((row) => row.id));
        } else {
            setSelectedIds([]);
        }
    };
    const columns = [
        createStringDataField("select", "Select", {
            sortable: false,
            renderCell: (params: any) => (
                <Checkbox
                    checked={selectedIds.includes(params.row.id)}
                    onChange={() => handleSelectToggle(params.row.id)}
                />
            ),
        }),
        createStringDataField("resourceId", "Resource Id", { sortable: true }),
        createStringDataField("resourceType", "Resource Type", {
            sortable: true,
        }),
        createStringDataField("attachMentType", "Attachment Type", {
            sortable: true,
        }),
    ];
    const renderSelectAction = (row: any) => [
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {/* Preview Icon */}
            <Tooltip title="Preview">
                <IconButton size="small" onClick={() => handlePreview(row)}>
                    <VisibilityIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </div>,
    ];

    const handleConfirm = async () => {
        if (selectedIds.length === 0) return;
        try {
            const selectedAttachments = attachments.filter((att) =>
                selectedIds.includes(att.id),
            );
            await onConfirm(selectedAttachments);
            // Reset
            setSelectedIds([]);
            onClose();
        } catch (error) {
            console.error("Error processing attachments:", error);
        }
    };

    return (
        <Dialog maxWidth="lg" fullWidth open={open} onClose={onClose}>
            <Loadable loading={isDownloading}>
                <DialogTitle>
                    <Grid container alignItems="center">
                        <Grid item xs={10}>
                            <Typography variant="h6">
                                Select Attachment
                            </Typography>
                        </Grid>
                        <Grid item xs={2} className="text-right">
                            <IconButton
                                aria-label="close"
                                onClick={onClose}
                                size="large"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                </DialogTitle>

                <DialogContent>
                    {!hasAttachments ? (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                padding: "20px",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                No Attachments available.
                            </Typography>
                        </div>
                    ) : (
                        <Box sx={{ width: "100%", overflow: "hidden" }}>
                            <Box
                                sx={{
                                    padding: "8px 16px",
                                    borderBottom: "1px solid #e0e0e0",
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                selectedIds.length ===
                                                    attachments.length &&
                                                attachments.length > 0
                                            }
                                            indeterminate={
                                                selectedIds.length > 0 &&
                                                selectedIds.length <
                                                    attachments.length
                                            }
                                            onChange={handleSelectAll}
                                        />
                                    }
                                    label={`Select All (${selectedIds.length} of ${attachments.length} selected)`}
                                />
                            </Box>                            
                            <XClientDataGrid
                                fields={columns}
                                getRowId={(row) => row.id}
                                data={attachments}
                                sortPtr={sortPtr}
                                loading={false}
                                actionColumnName="Preview"
                                actions={renderSelectAction}
                                hideFooter
                                sx={{
                                    "& .MuiDataGrid-columnHeaders": {
                                        width: "100%",
                                    },
                                    "& .MuiDataGrid-virtualScroller": {
                                        width: "100%",
                                    },
                                    // Highlight selected rows
                                    "& .MuiDataGrid-row": {
                                        "&.Mui-selected": {
                                            backgroundColor:
                                                "rgba(25, 118, 210, 0.08)",
                                        },
                                    },
                                }}
                                // Add row selection styling
                                getRowClassName={(params) =>
                                    selectedIds.includes(params.id as string)
                                        ? "Mui-selected"
                                        : ""
                                }
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions className="p-3">
                    <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button>
                    {hasAttachments && (
                        <Button
                            onClick={handleConfirm}
                            variant="contained"
                            disabled={selectedIds.length === 0 || isDownloading}
                        >
                            {selectedIds.length === 0
                                ? "Submit"
                                : `Submit (${selectedIds.length} selected)`}
                        </Button>
                    )}
                </DialogActions>
            </Loadable>
        </Dialog>
    );
}
